/**
 * How the screen crosses from one look to another - a lookup.
 *
 * A change of phase is a moment at the table, and it is immediate: no title
 * card, no wait. What marks it is A CROSS-FADE OF THE WHOLE SCREEN, old look
 * into new, whose length says what kind of moment it is - and, for the two
 * arrivals that should startle, one soft flash over it:
 *
 *   into Combat      quick, with a red flash
 *   out of Combat    the slowest: the red ebbs away
 *   into Stealth     the lights go down
 *   anything else    a short fade
 *   Stealth's ladder reaching Alarm    quick, with a yellow flash
 *   ...and leaving Alarm               the yellow drains off
 *
 * Nothing else is a crossing. The three blue rungs of Stealth and Combat's
 * danger steps are not: there the GROUND eases by itself (the app's
 * stylesheet), which can be interrupted and re-aimed mid-way where a
 * cross-fade cannot.
 *
 * Earlier (v9.0.0-v9.2.0) each crossing was an opaque veil thrown over the
 * screen at full strength; leaving Combat or Stealth read as a flash, and
 * leaving Alarm was an instant swap of the whole palette (user, 2026-10-05).
 *
 * Arriving - the first look a screen learns - is not a crossing.
 */
import type { Phase } from './types';

/** A flash laid over the cross-fade. */
type Flash = 'flash' | 'alarm';

export interface Crossing {
  /** How long the old look takes to become the new, in milliseconds. */
  fadeMs: number;
  /** A soft flash over it, for an arrival that should startle. */
  flash: Flash | null;
}

/** What a screen is wearing: the phase, and Stealth's rung where there is a ladder. */
export interface Look {
  phase: Phase | null;
  rung: string | null;
}

const FADE = { intoCombat: 450, outOfCombat: 1800, intoStealth: 900, other: 600, intoAlarm: 450, outOfAlarm: 900 };
const ALARM = 'alarm';

/** A change of phase. */
function phaseCrossing(from: Phase, to: Phase): Crossing {
  if (to === 'combat') return { fadeMs: FADE.intoCombat, flash: 'flash' };
  if (from === 'combat') return { fadeMs: FADE.outOfCombat, flash: null };
  return { fadeMs: to === 'stealth' ? FADE.intoStealth : FADE.other, flash: null };
}

/** The ladder moving within Stealth: only Alarm, either way, is a crossing. */
function rungCrossing(from: string | null, to: string | null): Crossing | null {
  if (from === to || from === null || to === null) return null;
  if (to === ALARM) return { fadeMs: FADE.intoAlarm, flash: 'alarm' };
  return from === ALARM ? { fadeMs: FADE.outOfAlarm, flash: null } : null;
}

/** The crossing from one look to the next, or null when there is none to play. */
export function crossingBetween(was: Look, now: Look): Crossing | null {
  if (!was.phase || !now.phase) return null;
  return was.phase === now.phase ? rungCrossing(was.rung, now.rung) : phaseCrossing(was.phase, now.phase);
}
